import { BestaLegsComponent } from '../components/BestaLegsComponent';
import { ComponentIds, ComponentUtils, HierarchyUtils, SelectableComponent, ReplaceAssemblyPartUtils, SelectedComponent, OutlinedComponent } from '@inter-ikea-core/core';
import { ProductType, ProductInfoComponent, PlannerEcsSystem } from '@inter-ikea-storageone/range';
/**
 * This system is responsible for actually performing the changes on the frame assembly when any leg change takes place.
 * The system will listen to changes to {@link BestaLegsComponent} and will build the frame assembly accordingly, with
 * correct legs (which can be merged) if placed on floor
 * When this system acts all data should already be calculated and this system is only responsible for building the assembly according to
 * that data.
 *
 * The legs must be configured in the range like this for the system to work:
 *      leg-1                   left-front
 *      leg-2                   left-back
 *      leg-3                   right-front
 *      leg-4                   right-back
 *      leg-5 to leg-8          the legs inbetween, can be replaced by support
 *                              legs (which is put on odd numbered leg-pos, even
 *                              will be empty)
 *      leg-shared-1            replaces leg-1 if aligned to the left
 *      leg-shared-2            replaces leg-4 if aligned to the right
 */
export class BestaFrameAssemblySystem extends PlannerEcsSystem {
    constructor(coreApi) {
        super(coreApi, 'BestaFrameAssemblySystem', BestaLegsComponent.id);
        this.mergableLegs = [
            '70472930',
            '10293564',
            '20293568',
            '60293566',
            '40293567',
            '80293565',
            '20489867',
            '70472930',
            '70489916',
            '40489913',
            '60489907',
            '00489910',
            '10489919',
            '50489922',
            '90489901',
            '30489904',
            '10513919',
            '20513914',
            '50513917',
            '60513912',
            '70513916',
            '90513915'
        ];
        this.legsRequiringSupport = ['50293595', '10293597', '20293592'];
        this.legsCantHaveSupport = ['pFramePad'];
        this.supportLeg = '50293604';
        this.coreApi = coreApi;
    }
    onAdd(entity, newLegs) {
        this.updateAssembly(entity, newLegs);
    }
    onUpdate(entity, oldLegs, newLegs) {
        this.updateAssembly(entity, newLegs);
    }
    onRemove() {
        // This component is never removed unless the whole entity is removed so we don't need to rebuild when that happens
    }
    addSelectedComponentToNewLegs() {
        // Remove all support legs and select the first valid leg which is not a support leg, doesn't matter which leg we select for now. We only care about that it's not a support leg.
        const legs = this.coreApi.entities.getByComponent(ComponentIds.LEG_COMPONENT_ID, 'besta');
        if (!legs || legs?.length === 0) {
            return;
        }
        const newLegToSelect = legs.find((e) => e.getComponent(ProductInfoComponent.id)?.productType ===
            ProductType.BESTA_LEG);
        newLegToSelect?.addComponent(new SelectedComponent());
        legs.forEach(leg => {
            leg.addComponent(new OutlinedComponent());
        });
    }
    updateAssembly(entity, legsComponent) {
        // We expect that these options are mutually explusive
        if (legsComponent.hasLegs)
            this.setLegs(entity, legsComponent.legProductRef, legsComponent.mergedLegsLeft, legsComponent.mergedLegsRight, legsComponent.useSupportLeg);
        else
            this.setNoExtraParts(entity);
    }
    setNoExtraParts(entity) {
        ReplaceAssemblyPartUtils.replaceProductParts(entity, this.generateAssemblyParts(''), this.coreApi.getServices());
    }
    //Workaround since adding the SelectableComponent in the component resolver doesn't work at the moment
    makeLegsSelectable(entity, entityManager) {
        const d = HierarchyUtils.descendants(entity);
        d.filter((c) => c.getComponent(ProductInfoComponent.id)?.productType ===
            ProductType.BESTA_LEG).forEach((leg) => entityManager.addComponent(leg, new SelectableComponent()));
    }
    setLegs(entity, selectedLeg, left, right, useSupportLeg) {
        const parts = this.generateAssemblyParts(selectedLeg);
        const canBeMerged = this.mergableLegs.includes(selectedLeg);
        const legsCannotBeShared = this.legsRequiringSupport.includes(selectedLeg);
        const legsCanHaveSupport = !this.legsCantHaveSupport.includes(selectedLeg);
        if (left && canBeMerged && !legsCannotBeShared) {
            parts[0].productRef = '';
            parts[1].productRef = '';
            parts[8].productRef = selectedLeg;
        }
        if (right && canBeMerged && !legsCannotBeShared) {
            parts[2].productRef = '';
            parts[3].productRef = '';
            parts[9].productRef = selectedLeg;
        }
        if (legsCanHaveSupport && (useSupportLeg || legsCannotBeShared)) {
            parts[4].productRef = this.supportLeg;
            parts[5].productRef = '';
            parts[6].productRef = this.supportLeg;
            parts[7].productRef = '';
        }
        const filterdParts = this.filterExistingParts(entity, parts);
        const legs = this.coreApi.entities.getByComponent(ComponentIds.LEG_COMPONENT_ID);
        const selectedLegEntity = legs.find(leg => {
            return leg.hasComponent(ComponentIds.SELECTED_COMPONENT_ID);
        });
        ReplaceAssemblyPartUtils.replaceProductParts(entity, filterdParts, this.coreApi.getServices());
        if (selectedLegEntity) {
            this.addSelectedComponentToNewLegs();
        }
        this.makeLegsSelectable(entity, this.coreApi.getServices().entityManager);
    }
    /**
     * Generates a default part map that can be used for all frame sizes
     * @param productRef leg productRef
     */
    generateAssemblyParts(productRef) {
        return [
            { partKey: 'leg-1', productRef: productRef },
            { partKey: 'leg-2', productRef: productRef },
            { partKey: 'leg-3', productRef: productRef },
            { partKey: 'leg-4', productRef: productRef },
            { partKey: 'leg-5', productRef: productRef },
            { partKey: 'leg-6', productRef: productRef },
            { partKey: 'leg-7', productRef: productRef },
            { partKey: 'leg-8', productRef: productRef },
            { partKey: 'leg-shared-1', productRef: '' },
            { partKey: 'leg-shared-2', productRef: '' }
        ];
    }
    /**
     * To make it easy for this system the generateAssemblyParts function returns all part mappings
     * that could exist for a BESTA frame. Some frames will however have fewer possible parts in their
     * assembly. If the frame has fewer parts defined in its range template,
     * PlacementService.replaceProductParts will ignore the mappings with other partKeys. But for some
     * frames (BESTA TV bench) we define 6 leg (1) parts in the template, even though the actual assembly
     * only has connectors for 5 of the legs. replaceProductParts doesn't take care of that case. Either it
     * needs to be handled in the range by having a separete template for 5-legged frames, or handled by code
     * as below.
     *
     * 1) Why do we use the same template for frames with 5 and 6 legs. The only reason is to have fewer
     * templates to maintain. If we split that template into two different ones, the code below is not needed.
     *
     * This method will, for each part mapping, check the assembly structure and see if the connector used
     * to build a part into the assembly actually exists in the assembly. If not, that part mapping is
     * filtered out. Exception is suspension rail parts that are not part of the assembly structure but
     * should not be filtered out.
     *
     * @param entity The root entity of the assembly
     */
    filterExistingParts(entity, partsMap) {
        const assemblyStructure = entity.getComponent(ComponentIds.ASSEMBLY_STRUCTURE_COMPONENT_ID);
        if (!assemblyStructure) {
            return partsMap;
        }
        const allConnectorsInHierarchy = HierarchyUtils.hierarchy(entity).flatMap((e) => ComponentUtils.connectors(e));
        return partsMap.filter(part => {
            const assemblyPart = this.assemblyPartForPartKey(assemblyStructure.parts, part.partKey);
            const connectorKey = assemblyPart?.snapInfo?.connectorOnHost;
            if (connectorKey) {
                const matchingConnector = allConnectorsInHierarchy.find((c) => c.key === connectorKey);
                if (matchingConnector) {
                    return true;
                }
            }
            return false;
        });
    }
    assemblyPartForPartKey(assemblyParts, partKey) {
        if (assemblyParts == null) {
            return null;
        }
        const matchingParts = assemblyParts
            .map((part) => {
            if (part.partKey === partKey) {
                return part;
            }
            else {
                return this.assemblyPartForPartKey(part.children, partKey);
            }
        })
            .filter(part => part != null);
        if (matchingParts.length > 0) {
            return matchingParts[0];
        }
        else {
            return null;
        }
    }
}
//# sourceMappingURL=BestaFrameAssemblySystem.js.map